import { groupBy, intersection, isEmpty, omit } from 'lodash/fp';

import { COMORBID_CAUSE_ID_KEY, isTimeUnitKey } from '@portal/common/models/data-key';
import {
    DataCollectionResource,
    DataCondition,
    DataFilters,
    DataGranularity,
    DataType,
    Organization,
    TimeUnitKey,
} from '@portal/common/types';
import { getConditionsDataTypes } from '@portal/common/utility/filters-helpers';

import { buildDataTypeRequestFilters, countFilterCombinations } from '../data-loader';
import { applyNowcastToGranularity } from './nowcasts';

// `filters` must be the defaults-merged selection, or the count expands
// dimensions the request narrows
export type CombinedFiltersCountInputs = {
    conditions: DataCondition[];
    filters: DataFilters | null;
    granularity: DataGranularity;
    dataCollection: DataCollectionResource | null;
    organization: Organization;
    roundId: number;
    isNowcastsActive: boolean;
    lastPublishedYear: number | null;
    hasNullComorbidity: boolean;
};

export type CombinedFiltersCountOverrides = {
    conditions?: DataCondition[];
    filters?: DataFilters | null;
    // nowcasts widen (and clip) the year granularity, so they change the count
    isNowcastsActive?: boolean;
};

export type CombinedFiltersCountOptions = {
    ignoreTimeUnits?: boolean;
};

export const getFiltersSelectionCount = (
    filters: DataFilters | null,
    granularity: DataGranularity,
    ignoreTimeUnits: boolean = false
): number =>
    Object.entries(filters || {})
        .filter(([key]) => !(ignoreTimeUnits && isTimeUnitKey(key as TimeUnitKey)))
        .reduce(
            (total, [key, values]) =>
                total * (intersection(granularity?.[key] || [], values || []).length || 1),
            1
        );

// one request per data type, so the total is a sum of per-data-type products
export const countCombinedFilters = (
    {
        conditions,
        filters,
        granularity,
        dataCollection,
        organization,
        roundId,
        isNowcastsActive,
        lastPublishedYear,
        hasNullComorbidity,
    }: CombinedFiltersCountInputs,
    { ignoreTimeUnits = false }: CombinedFiltersCountOptions = {}
): number => {
    if (isEmpty(conditions) || !dataCollection?.datasets) {
        return 0;
    }

    const dataTypes = getConditionsDataTypes(conditions);
    const requestGranularity = isNowcastsActive
        ? applyNowcastToGranularity(
              granularity,
              organization,
              dataCollection,
              dataTypes,
              roundId,
              lastPublishedYear
          )
        : granularity;

    return Object.entries(groupBy(({ data_type }) => data_type, conditions)).reduce(
        (total, [dataType, dataTypeConditions]) => {
            const requestFilters = buildDataTypeRequestFilters(
                dataCollection,
                dataType as DataType,
                dataTypeConditions,
                filters,
                requestGranularity
            );

            if (!requestFilters) {
                return total;
            }

            const omittedKeys = Object.keys(requestFilters).filter(
                (key) =>
                    (hasNullComorbidity && key === COMORBID_CAUSE_ID_KEY) ||
                    (ignoreTimeUnits && isTimeUnitKey(key as TimeUnitKey))
            );

            return total + countFilterCombinations(omit(omittedKeys, requestFilters));
        },
        0
    );
};
